import { SelectOption } from 'components/inputs/BaseSelect';
import Immutable from 'immutable';
import React from 'react';
import { ActionMeta, ValueType } from 'react-select/src/types';
import MultiSelect, { MultiSelectOption } from './MultiSelect';

interface Props<O> {
  id: string;
  options: Immutable.OrderedSet<O>;
  values: Immutable.OrderedSet<O>;
  onChange: (values: Immutable.OrderedSet<O>) => void;
  selector: (item: O) => string;
  defaultValue?: SelectOption;
  placeholder?: string;
  inputId?: string;
}

class OrderedSetMultiSelect<O> extends React.Component<Props<O>> {
  static defaultProps = {
    options: Immutable.OrderedSet(),
    values: Immutable.OrderedSet(),
    selector: (item: string): string => item,
  };

  getUpdatedOptions = (
    selectedOptions: ValueType<MultiSelectOption, boolean>
  ): Immutable.OrderedSet<O> => {
    const { options, selector } = this.props;

    if (Array.isArray(selectedOptions)) {
      const arr: readonly MultiSelectOption[] = selectedOptions;

      const optionVals = arr.map((mso) => mso.label);

      const selOptions = options.filter((o) => {
        const s = selector(o);
        return optionVals.some((ov) => ov === s);
      });

      return selOptions;
    }

    return Immutable.OrderedSet<O>();
  };

  handleChange = (
    selectedOptions: ValueType<MultiSelectOption, boolean>,
    actionData: ActionMeta<MultiSelectOption>
  ): void => {
    const { onChange } = this.props;

    // Each case should return the updated options, in OrderedSet<O> form.
    switch (actionData.action) {
      case 'select-option':
        onChange(this.getUpdatedOptions(selectedOptions));
        break;
      case 'remove-value':
        onChange(this.getUpdatedOptions(selectedOptions));
        break;
      case 'clear':
        onChange(Immutable.OrderedSet<O>());
        break;
      default:
        break;
    }
  };

  toMultiSelect = (items: Immutable.OrderedSet<O>): MultiSelectOption[] => {
    const { selector } = this.props;

    return items
      .map(selector)
      .map((value: string): MultiSelectOption => ({ value, label: value }))
      .toArray();
  };

  render(): React.ReactNode {
    const { values, options, defaultValue, placeholder, id, inputId } =
      this.props;
    return (
      <MultiSelect
        id={id}
        inputId={inputId}
        value={this.toMultiSelect(values)}
        options={this.toMultiSelect(options)}
        onChange={this.handleChange}
        defaultValue={defaultValue}
        placeholder={placeholder}
      />
    );
  }
}

export default OrderedSetMultiSelect;
